import './TopUpTonSheet.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { balanceActions, balanceSelector } from '$store/balance';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { mainActions, mainSelector } from '$store/main';
import vkQr from '@vkontakte/vk-qr';
import TonWeb from 'tonweb';
import { Button, LottieIcon } from '$uikit';
import { Api } from '$api';
import { batchActions } from '$store';
import { formatCurrency } from '$utils';
import { CurrencyEnum } from '$store/models';

export const TopUpTonSheetInner: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { topUpAddress, balance } = useSelector(mainSelector);
  const { requireTopUpTonAmount } = useSelector(balanceSelector);
  const user = useCurrentUserInfo();
  const initialBalance = useRef(balance);
  const timer = useRef<any>(0);
  const [topUpped, setTopUpped] = useState(false);

  const refreshBalance = useCallback(() => {
    Api.get('/balance/ton/get')
      .then((resp) => {
        const data: any = resp;
        if (data.balance > initialBalance.current) {
          setTopUpped(true);
          dispatch(
            batchActions(
              balanceActions.setBalance(data.balance),
              mainActions.setBalance(data.balance),
            ),
          );
        } else {
          timer.current = setTimeout(() => refreshBalance(), 5000);
        }
      })
      .catch(() => {
        timer.current = setTimeout(() => refreshBalance(), 5000);
      });
  }, [initialBalance, timer]);

  useEffect(() => {
    timer.current = setTimeout(() => refreshBalance(), 5000);
    return () => clearTimeout(timer.current);
  }, []);

  const handleClose = useCallback(() => {
    dispatch(
      balanceActions.setTopUpTonActive({
        topUpActive: false,
      }),
    );
  }, [dispatch]);

  const hexID = useMemo(() => {
    return new Number(user.id).toString(16);
  }, []);

  const href = useMemo(() => {
    console.log('requireTopUpTonAmount', requireTopUpTonAmount);
    return `https://app.tonkeeper.com/transfer/${topUpAddress}?amount=${TonWeb.utils.toNano(
      Math.ceil(requireTopUpTonAmount),
    )}&text=${hexID}`;
  }, [hexID, requireTopUpTonAmount, topUpAddress]);

  const qrCode = useMemo(() => {
    return vkQr.createQR(href, {
      qrSize: 200,
      isShowLogo: false,
      isShowBackground: true,
    });
  }, [href]);

  function renderContent() {
    if (topUpped) {
      return (
        <>
          <LottieIcon name="congratulation" />
          <div className="Tips__success__title">{t('top_up_tons_success')}</div>
          <Button className="Tips__success__close" onClick={handleClose}>
            {t('tips_close')}
          </Button>
        </>
      );
    }

    return (
      <>
        <div className="TopUpTonSheet__qr">
          <div dangerouslySetInnerHTML={{ __html: qrCode }} />
        </div>
        {requireTopUpTonAmount > 0 && (
          <div className="TopUpTonSheet__caption">
            {t('top_up_tons_require_amount', {
              amount: formatCurrency(Math.ceil(requireTopUpTonAmount), CurrencyEnum.TON),
            })}
          </div>
        )}
        <div className="TopUpTonSheet__button">
          <Button href={href} target="_blank">
            {t('top_up_tons_open_wallet')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <BottomSheet
      title={topUpped ? null : t('top_up_tons_title')}
      onClose={handleClose}
      closeButtonShown={!topUpped}
    >
      <div className="TopUpTonSheet_wrap">{renderContent()}</div>
    </BottomSheet>
  );
};

export const TopUpTonSheet: FC = () => {
  const { topUpTonActive } = useSelector(balanceSelector);
  if (!topUpTonActive) {
    return null;
  }

  return <TopUpTonSheetInner />;
};
